import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cva } from 'class-variance-authority';
import { cn } from './cn.js';
/**
 * The status vocabulary, in one place: the legacy `StatusBadge` values,
 * unchanged. A status string not in this map renders `neutral`.
 */
export const STATUS_TONES = {
    online: 'success',
    active: 'success',
    claimed: 'success',
    discovered: 'success',
    granted: 'success',
    applied: 'success',
    offline: 'neutral',
    inactive: 'neutral',
    retired: 'neutral',
    stopped: 'neutral',
    unknown: 'neutral',
    pending: 'neutral',
    decommissioned: 'neutral',
    error: 'destructive',
    missing: 'destructive',
    expired: 'destructive',
    lost: 'destructive',
    revoked: 'destructive',
    forced: 'destructive',
    critical: 'destructive',
    updating: 'info',
    commissioning: 'info',
    claiming: 'info',
    booting: 'info',
    provisioning: 'info',
    connecting: 'info',
    not_yet_valid: 'info',
    virtual: 'info',
    maintenance: 'warning',
    orphaned: 'warning',
    unassigned: 'warning',
    suspended: 'warning',
    disabled: 'warning',
    outdated: 'warning',
    unmapped: 'warning',
    identify: 'primary',
};
/** The tone for a status string (`not yet valid` and `not_yet_valid` both resolve). */
export function statusTone(status) {
    if (!status)
        return 'neutral';
    return STATUS_TONES[status.trim().toLowerCase().replace(/[\s-]+/g, '_')] ?? 'neutral';
}
const statusTagVariants = cva('inline-flex items-center gap-1 whitespace-nowrap rounded-md [&_svg]:size-3', {
    variants: {
        tone: {
            success: 'bg-success-soft text-success-soft-foreground',
            neutral: 'bg-neutral-soft text-neutral-soft-foreground',
            destructive: 'bg-destructive-soft text-destructive-soft-foreground',
            info: 'bg-info-soft text-info-soft-foreground',
            warning: 'bg-warning-soft text-warning-soft-foreground',
            primary: 'bg-primary-soft text-primary-text',
        },
        size: {
            /** 11 px uppercase, the default in cells. */
            sm: 'px-1.5 py-0.5 font-display text-micro font-semibold uppercase',
            /** 12 px sentence case, for sheets and headers. */
            md: 'px-2 py-0.5 text-caption font-medium',
        },
    },
    defaultVariants: {
        tone: 'neutral',
        size: 'sm',
    },
});
const dotToneClass = {
    success: 'bg-success',
    neutral: 'bg-muted-foreground',
    destructive: 'bg-destructive',
    info: 'bg-info',
    warning: 'bg-warning-text',
    primary: 'bg-primary',
};
/** A plain 8 px status dot. */
export function StatusDot({ tone = 'neutral', className }) {
    return _jsx("span", { "aria-hidden": "true", className: cn('inline-block size-2 shrink-0 rounded-full', dotToneClass[tone], className) });
}
/** The online dot with its halo: opacity and scale of the ring only, 2 s loop. */
export function LiveDot({ tone = 'success', className }) {
    return (_jsx("span", { "aria-hidden": "true", className: cn('relative inline-block size-2 shrink-0 rounded-full', dotToneClass[tone], className), children: _jsx("span", { className: cn('absolute -inset-1 rounded-full border-2 opacity-0 animate-live-ring', {
                'border-success': tone === 'success',
                'border-muted-foreground': tone === 'neutral',
                'border-destructive': tone === 'destructive',
                'border-info': tone === 'info',
                'border-warning-text': tone === 'warning',
                'border-primary': tone === 'primary',
            }) }) }));
}
/**
 * Every tag is a word; the tone only reinforces it. `micro` uppercase in
 * Manrope on a `*-soft` tint by default (`sm`), or 12 px sentence case (`md`).
 */
export function StatusTag({ className, tone, status, size, icon: Icon, live, dot, children, ...props }) {
    const resolvedTone = tone ?? statusTone(status);
    const label = children ?? status?.replace(/_/g, ' ');
    return (_jsxs("span", { "data-slot": "status-tag", "data-tone": resolvedTone, className: cn(statusTagVariants({ tone: resolvedTone, size }), className), ...props, children: [live ? _jsx(LiveDot, { tone: resolvedTone }) : dot ? _jsx(StatusDot, { tone: resolvedTone }) : null, Icon && _jsx(Icon, { "aria-hidden": "true" }), label] }));
}
export { statusTagVariants };
//# sourceMappingURL=status-tag.js.map